import { alpha, Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        chatbot: {
            position: "sticky",
            bottom: 10,
            width: "100%",
            margin: theme.spacing(0, 3),
            border: "1px solid rgba(173, 70, 255, 0.6)",
            boxShadow: "0 -8px 48px 0 rgba(88, 28, 135, 0.6)",
            borderRadius: "16px",
            zIndex: 1,
            background: `linear-gradient(
                180deg, ${theme.palette.gradients.g08.n1} 0%,
                ${theme.palette.gradients.g08.n2} 40%,
                ${theme.palette.gradients.g08.n3} 70%,
                ${theme.palette.gradients.g08.n4} 100%)`,
        },
        chatbotCompact: {
            width: 484,
            marginLeft: "auto",
        },
        chatbotOpenCompact: {
            height: "calc(100vh - 100px)",
            display: "flex",
            flexDirection: "column",
        },
        chatbotMaximized: {
            position: "fixed",
            left: `calc(275px + ${theme.spacing(3)})`,
            right: theme.spacing(3),
            bottom: theme.spacing(2),
            width: "auto",
            height: "calc(100vh - 32px)",
            margin: 0,
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
        },
        header: {
            height: 75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "16px",
            padding: theme.spacing(2),
        },
        headerCompact: {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
        headerClickable: {
            cursor: "pointer",
        },
        headerContent: {
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
        },
        avatarContainerMedium: {
            backgroundColor: theme.palette.others.lightPurple,
            borderRadius: "16px",
            marginRight: theme.spacing(1.5),
        },
        avatarContainerSmall: {
            backgroundColor: theme.palette.others.lightPurple,
            marginRight: theme.spacing(1.5),
        },
        avatarUser: {
            marginLeft: theme.spacing(1.5),
            backgroundColor: theme.palette.others.paleBlueGray,
            color: theme.palette.others.darkBluegray,
            fontSize: 12,
            fontWeight: 400,
        },
        title: {
            fontSize: 18,
            fontWeight: 700,
            color: theme.palette.others.white,
        },
        titleOffline: {
            fontSize: 18,
            fontWeight: 700,
            color: theme.palette.text.disabled,
        },
        titleCompact: {
            fontSize: 16,
        },
        titleOpenCompacted: {
            fontSize: 14,
        },
        statusRow: {
            display: "flex",
            alignItems: "center",
        },
        statusIndicator: {
            backgroundColor: theme.palette.info.main,
            width: 8,
            height: 8,
            borderRadius: "50px",
            boxShadow: "0 0 8px 0 rgba(56, 189,248, 0.6)",
        },
        loadingIndicator: {
            backgroundColor: theme.palette.others.palePink,
            width: 8,
            height: 8,
            borderRadius: "50px",
            opacity: 0.5,
        },
        statusIndicatorOffline: {
            backgroundColor: theme.palette.neutrals.main,
            opacity: 0.9,
            boxShadow: "0 0 8px 0 rgba(190, 193, 195, 0.60)",
        },
        statusText: {
            marginLeft: theme.spacing(1.25),
            color: theme.palette.others.white,
            fontSize: 14,
        },
        statusTextOffline: {
            color: theme.palette.text.disabled,
        },
        headerActions: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "auto",
            flexShrink: 0,
        },
        iconButton: {
            padding: 0,
        },
        iconButtonCompact: {
            "& .MuiSvgIcon-root": {
                fontSize: 14,
            },
        },
        tabletButton: {
            marginLeft: theme.spacing(1.5),
            marginBottom: theme.spacing(1),
        },
        maximizeButtonCompact: {
            marginRight: theme.spacing(1),
        },
        tabletButtonCompact: {
            margin: theme.spacing(0, 1.5, 0.5, 0),
        },
        closeButton: {
            marginLeft: theme.spacing(2.5),
        },
        closeButtonCompact: {
            marginLeft: 0,
        },
        body: {
            height: 320,
            backgroundColor: theme.palette.others.white,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        },
        bodyFillHeight: {
            height: "auto",
            flex: "auto",
        },
        messagesContainer: {
            flex: 1,
            overflow: "auto",
            padding: theme.spacing(2.5, 4.5, 0, 2.5),
            "&::-webkit-scrollbar-track": {
                borderRadius: 8,
                background: "rgba(244, 233, 255, 0.44)",
            },
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.others.palePurple,
                borderRadius: 8,
            },
        },
        footer: {
            padding: theme.spacing(1, 3),
            background: `linear-gradient(90deg,
                ${theme.palette.gradients.g09.n1} 0%,
                ${theme.palette.gradients.g09.n2} 50%,
                ${theme.palette.gradients.g09.n3} 100%)`,
            display: "flex",
            alignItems: "center",
        },
        input: {
            "& .MuiOutlinedInput-root": {
                minHeight: 42,
                borderRadius: 31,
                color: theme.palette.common.white,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                border: "none",
                "& .MuiInputBase-input": {
                    color: theme.palette.common.white,
                    opacity: 1,
                    "&::placeholder": {
                        color: theme.palette.common.white,
                        opacity: 0.5,
                    },
                    "&:focus::placeholder": {
                        opacity: 0,
                    },
                },
                "& .MuiInputBase-inputMultiline": {
                    overflowY: "auto",
                    "&::-webkit-scrollbar-track": {
                        borderRadius: 8,
                        background: "rgba(244, 233, 255, 0.44)",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: theme.palette.others.palePurple,
                    },
                },
            },
        },
        footerButton: {
            height: 36,
            width: 36,
            borderRadius: "50%",
            color: theme.palette.others.white,
            padding: theme.spacing(1),
            "&.Mui-disabled": {
                opacity: 0.5,
                color: theme.palette.others.white,
            },
            "&:nth-of-type(2)": {
                marginLeft: theme.spacing(2),
                marginRight: theme.spacing(1),
            },
        },
        sendButton: {
            marginLeft: theme.spacing(1.5),
            background: `linear-gradient(135deg,
                ${theme.palette.gradients.g11.n1} 0%,
                ${theme.palette.gradients.g11.n2} 100%)`,
            padding: theme.spacing(1),
        },
        stopButton: {
            background: `linear-gradient(135deg, 
                ${theme.palette.gradients.g12.n1} 0%, 
                ${theme.palette.gradients.g12.n2} 100%)`,
        },
        sendIcon: {
            marginLeft: theme.spacing(1),
            marginTop: theme.spacing(1),
        },
        otherButton: {
            border: `1px solid ${theme.palette.others.white}`,
            "&.Mui-disabled": {
                border: `1px solid ${theme.palette.others.white}`,
            },
        },
        escapeHint: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: theme.spacing(1),
            paddingTop: theme.spacing(1),
            height: 32,
        },
        escapeKeyContainer: {
            borderRadius: 4,
            border: "1px solid rgba(194, 122, 255, 0.30)",
            padding: theme.spacing(0.25, 0.75),
            margin: theme.spacing(0, 0.25),
            backgroundColor: theme.palette.others.purpleBlue,
        },
        escapeHintText: {
            fontSize: 12,
            color: theme.palette.others.palePurple,
        },
        messageWrapper: {
            padding: theme.spacing(2),
            borderRadius: 14,
            border: "1px solid rgba(233, 212, 255, 0.60)",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10);",
            whiteSpace: "normal",
            overflowX: "auto",
            "&::-webkit-scrollbar-track": {
                borderRadius: 8,
                background: "rgba(244, 233, 255, 0.44)",
            },
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.others.palePurple,
            },
            "& h3": {
                margin: theme.spacing(1, 0, 0.5, 0),
                fontSize: 16,
            },
            "& p": {
                margin: theme.spacing(0.5, 0),
            },
            "& ul, & ol": {
                margin: theme.spacing(1, 0),
            },
            "& li": {
                margin: theme.spacing(0.5, 0),
            },
            "& table": {
                width: "auto",
                border: `1px solid ${theme.palette.others.paleLavender}`,
                borderRadius: "0 0 14px 14px",
                borderSpacing: 0,
                tableLayout: "fixed",
            },
            "& thead": {
                background: `linear-gradient(90deg, 
                ${theme.palette.gradients.g13.n1} 0%, 
                ${theme.palette.gradients.g13.n2} 100%)`,
            },
            "& th, & td": {
                padding: theme.spacing(1, 1.5),
                fontSize: 12,
                fontWeight: 600,
                textAlign: "left",
                borderBottom: `1px solid ${theme.palette.others.paleLavender}`,
            },
            "& th": {
                color: theme.palette.others.darkViolet,
            },
            "& th:first-child": {
                color: "rgba(49, 65, 88, 1)",
            },
            "& td": {
                color: "rgba(49, 65, 88, 0.70)",
            },
            "& td:first-child": {
                color: "rgba(49, 65, 88, 1)",
                fontWeight: 500,
            },
            "& tbody tr:last-child td": {
                borderBottom: "none",
            },
        },
        assistantMessage: {
            width: "100%",
            backgroundColor: theme.palette.others.white,
            padding: theme.spacing(2, 1, 2, 2),
            fontSize: 16,
            lineHeight: "24px",
        },
        userMessage: {
            padding: theme.spacing(1.25, 2),
            background: `linear-gradient(135deg, ${theme.palette.gradients.g14.n1} 0%, ${theme.palette.gradients.g14.n2} 100%)`,
            borderRadius: "14px 6px 14px 14px",
            color: theme.palette.others.white,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -2px rgba(0, 0, 0, 0.10)",
            fontSize: 14,
            overflowWrap: "break-word",
        },
        feedbackContainer: {
            backgroundColor: theme.palette.others.white,
            padding: theme.spacing(0, 4, 0, 2.5),
            zIndex: 1,
            marginTop: "auto",
            height: 36,
        },
        feedbackContent: {
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
        },
        feedbackButton: {
            color: theme.palette.secondary.main,
            textDecoration: "underline",
            textUnderlineOffset: "4px",
            padding: 0,
        },
        tooltip: {
            color: theme.palette.text.secondary,
            background: theme.palette.background.grey,
            boxShadow:
                "0 1px 0 0 rgba(0, 0, 0, 0.03), 0 4px 4px 0 rgba(0, 0, 0, 0.05), 0 10px 10px 0 rgba(0, 0, 0, 0.10)",
        },
        tooltipArrow: {
            color: theme.palette.background.grey,
        },
        loadingLegend: {
            marginLeft: theme.spacing(1),
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.others.purpleBlue,
        },
        betaTagContainer: {
            borderRadius: 3,
            border: `0.75px solid ${alpha(theme.palette.others.white, 0.7)}`,
            padding: theme.spacing(0, 0.5),
            marginLeft: theme.spacing(1),
            backgroundColor: alpha(theme.palette.others.electricViolet, 0.25),
        },
        betaTagText: {
            fontSize: 9,
            fontWeight: 600,
            color: theme.palette.others.softLavender,
        },
        initialLoadingWrapper: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
        },
        messageLoadingText: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            marginBottom: theme.spacing(1.5),
            borderLeft: "1px solid #E9D4FF99",
            paddingLeft: theme.spacing(2),
            marginLeft: theme.spacing(3),
            height: 52,
        },
        "@keyframes swingLight": {
            "0%": {
                transform: "translateX(0%) rotate(-14deg)",
            },
            "100%": {
                transform: "translateX(0%) rotate(20deg)",
            },
        },
        "@keyframes pulseLight": {
            "0%": {
                opacity: 0.45,
            },

            "50%": {
                opacity: 0.75,
            },

            "100%": {
                opacity: 0.45,
            },
        },
        lightHouseAnimation: {
            position: "relative",
            display: "inline-block",
            "& img:first-of-type": {
                position: "absolute",
                top: "3%",
                left: "20%",
                transformOrigin: "center left",
                transform: "translateX(-50%) rotate(0deg)",
                animation: "$swingLight 1.8s ease-in-out infinite alternate, $pulseLight 1s ease-in-out infinite",
                width: "100%",
                height: "auto",
            },
            "& img:last-of-type": {
                display: "block",
                width: "100%",
                height: "auto",
            },
        },
        highlightedBold: {
            color: "#470A74",
        },
    })
);

export default useStyles;
